import dayjs from 'dayjs'
import { has, isEmpty, isNull, isNumber, isObject, memoize } from 'lodash'
import { computed, nextTick, ref, toRefs, toValue, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { BINARY_OPTIONS, BINARY_VALUES } from '@/utils/components-configurations/okr-grade'
import { createDatesRange, DATE_UNITS } from '@/utils/date'
import { TARGET_PERIOD_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { memoizeFormatNumber } from '@/utils/memoizations'
import { getResolvedUnitForChart } from '@/utils/objective-chart'
import {
  DEFAULT_THRESHOLD_PERIOD_GRANULATION,
  getThresholdConditionSymbol,
  THRESHOLD_PERIOD_GRANULATION
} from '@/utils/okr-element-values'
import { getSimplifiedNumber, PLURALIZATION_KEYS } from '@/utils/pluralization'

const createNestedColor = (
  baseColor = '--primary-color-next',
  hue = 0,
  saturation = 0,
  lightness = 0
) => {
  if (!hue && !saturation && !lightness) return `var(${baseColor})`

  const formatColorShift = (channel = 'h', value = 0) => {
    if (!value) return channel

    const sign = value > 0 ? '+' : '-'

    return `calc(${channel} ${sign} ${Math.abs(value)})`
  }

  const newHue = formatColorShift('h', hue)
  const newSaturation = formatColorShift('s', saturation)
  const newLightness = formatColorShift('l', lightness)

  return `hsl(from var(${baseColor}) ${newHue} ${newSaturation} ${newLightness})`
}

export const COLORS_KEYS = {
  PROGRESS_MODULE_BACKGROUND_COLOR: '--pm-background-color',
  PROGRESS_MODULE_TEXT_BACKGROUND_COLOR: '--pm-text-background-color',
  PROGRESS_MODULE_TEXT_COLOR: '--pm-text-color',
  PROGRESS_MODULE_TEXT_IN_FUTURE_COLOR: '--pm-text-in-future-color',
  PROGRESS_MODULE_BORDER_COLOR: '--pm-border-color',
  PROGRESS_MODULE_BORDER_HOVER_COLOR: '--pm-border-hover-color',
  PROGRESS_MODULE_DIVIDER_SIZE: '--pm-divider-size',
  ELEMENT_PROGRESS_TABLE_TEXT_COLOR: 'ept-text-color'
}

const {
  PROGRESS_MODULE_BACKGROUND_COLOR,
  PROGRESS_MODULE_TEXT_BACKGROUND_COLOR,
  PROGRESS_MODULE_TEXT_COLOR,
  PROGRESS_MODULE_TEXT_IN_FUTURE_COLOR,
  PROGRESS_MODULE_BORDER_COLOR,
  PROGRESS_MODULE_BORDER_HOVER_COLOR,
  PROGRESS_MODULE_DIVIDER_SIZE,
  ELEMENT_PROGRESS_TABLE_TEXT_COLOR
} = COLORS_KEYS

export const ELEMENT_PROGRESS_STATUSES = {
  NO_VALUE: {
    statusId: 0,
    colors: {
      [PROGRESS_MODULE_BACKGROUND_COLOR]: 'var(--white)',
      [PROGRESS_MODULE_TEXT_COLOR]: 'var(--dark-3)',
      [PROGRESS_MODULE_TEXT_IN_FUTURE_COLOR]: 'var(--grey-1-next)',
      [PROGRESS_MODULE_TEXT_BACKGROUND_COLOR]: 'var(--grey-3-next)',
      [PROGRESS_MODULE_BORDER_COLOR]: 'var(--grey-2-next)',
      [PROGRESS_MODULE_BORDER_HOVER_COLOR]: 'var(--dark-2)',
      [PROGRESS_MODULE_DIVIDER_SIZE]: '1px',
      [ELEMENT_PROGRESS_TABLE_TEXT_COLOR]: 'var(--dark-3)'
    }
  },
  SUCCEED: {
    statusId: 1,
    colors: {
      [PROGRESS_MODULE_BACKGROUND_COLOR]: 'var(--grade-high-color-next-0)',
      [PROGRESS_MODULE_TEXT_COLOR]: 'var(--grade-high-color-next-600)',
      [PROGRESS_MODULE_TEXT_IN_FUTURE_COLOR]: 'var(--grade-high-color-next-600)',
      [PROGRESS_MODULE_TEXT_BACKGROUND_COLOR]: 'var(--grade-high-color-next-100)',
      [PROGRESS_MODULE_BORDER_COLOR]: 'var(--grade-high-color-next)',
      [PROGRESS_MODULE_BORDER_HOVER_COLOR]: 'var(--grade-high-color-next)',
      [PROGRESS_MODULE_DIVIDER_SIZE]: '2px',
      [ELEMENT_PROGRESS_TABLE_TEXT_COLOR]: 'var(--grade-high-color-next)'
    }
  },
  FAILED: {
    statusId: 2,
    colors: {
      [PROGRESS_MODULE_BACKGROUND_COLOR]: 'var(--grade-low-color-next-0)',
      [PROGRESS_MODULE_TEXT_COLOR]: 'var(--grade-low-color-next-600)',
      [PROGRESS_MODULE_TEXT_IN_FUTURE_COLOR]: 'var(--grade-low-color-next-600)',
      [PROGRESS_MODULE_TEXT_BACKGROUND_COLOR]: 'var(--grade-low-color-next-100)',
      [PROGRESS_MODULE_BORDER_COLOR]: 'var(--grade-low-color-next)',
      [PROGRESS_MODULE_BORDER_HOVER_COLOR]: 'var(--grade-low-color-next)',
      [PROGRESS_MODULE_DIVIDER_SIZE]: '2px',
      [ELEMENT_PROGRESS_TABLE_TEXT_COLOR]: 'var(--grade-low-color-next)'
    }
  }
}

export const ELEMENT_PROGRESS_GRADE_CELL_COLORS = {
  [PROGRESS_MODULE_BACKGROUND_COLOR]: createNestedColor('--grey-3-next', -0, 0, 2), // ~ #FAFBFB
  [PROGRESS_MODULE_TEXT_COLOR]: 'var(--dark-1)',
  [PROGRESS_MODULE_TEXT_IN_FUTURE_COLOR]: 'var(--grey-1-next)',
  [PROGRESS_MODULE_TEXT_BACKGROUND_COLOR]: 'var(--grey-3-next)',
  [PROGRESS_MODULE_BORDER_COLOR]: 'var(--grey-2-next)',
  [PROGRESS_MODULE_BORDER_HOVER_COLOR]: 'var(--grey-2-next)',
  [PROGRESS_MODULE_DIVIDER_SIZE]: '1px',
  [ELEMENT_PROGRESS_TABLE_TEXT_COLOR]: 'var(--dark-1)'
}

export const ELEMENT_PROGRESS_VALUE_CELL_COLORS = {
  ...ELEMENT_PROGRESS_GRADE_CELL_COLORS
}

export const ELEMENT_PROGRESS_CELL_VIEW_TYPES = {
  PLACEHOLDER: 0,
  VALUE: 1,
  GRADE: 2,
  VALUE_AND_THRESHOLD: 3
}

export const usePeriodGranulation = ({ pluralizeOptions = false } = {}) => {
  const { t } = useI18n()
  const getPeriodGranulationOptions = computed(() => {
    return Object.values(THRESHOLD_PERIOD_GRANULATION).map(item => {
      return {
        ...item,
        //  after transform, we get 0 for false and 1 for true,
        //  then we add 1 to get, so for false we have 1 for singular and 2 for plural
        label: t(item.label, Number(pluralizeOptions) + 1)
      }
    })
  })

  return {
    getPeriodGranulationOptions
  }
}

export const useInlineUpdate = ({ props = {} } = {}) => {
  const { updatable } = toRefs(props)
  const isShowInlineUpdateForm = ref(false)
  const editInitialised = ref(false)

  const onTriggerClick = () => {
    if (updatable.value) {
      if (!editInitialised.value) {
        editInitialised.value = true
      }

      isShowInlineUpdateForm.value = !isShowInlineUpdateForm.value
    }
  }

  watch(isShowInlineUpdateForm, newValue => {
    if (!newValue) {
      requestAnimationFrame(() => {
        nextTick(() => {
          editInitialised.value = false
        })
      })
    }
  })

  const handleInlineUpdateError = error => {
    handleError({ error })
  }

  return {
    onTriggerClick,
    isShowInlineUpdateForm,
    editInitialised,
    handleInlineUpdateError
  }
}

export const resolveProgressValue = ({ value, maxValue = 1e6 } = {}) => {
  if (Math.abs(value) > Math.abs(maxValue)) {
    return getSimplifiedNumber(value)[PLURALIZATION_KEYS.SIMPLIFIED]
  }

  return memoizeFormatNumber({ value })
}

export const useProgressCell = ({ data = {}, maxValue = 1e6, showUnitForValue = true } = {}) => {
  const isNoValue = computed(() => {
    return isNull(toValue(data).value)
  })

  const displayValue = computed(() => {
    const { value, unit } = toValue(data)

    const resolvedUnit = showUnitForValue ? getResolvedUnitForChart(unit) : ''
    return `${resolveProgressValue({ value, maxValue })}${resolvedUnit}`
  })

  const displayThreshold = computed(() => {
    const { thresholdConditionTypeId, threshold } = toValue(data)

    const symbol = getThresholdConditionSymbol({
      conditionId: thresholdConditionTypeId
    })

    return `${symbol} ${resolveProgressValue({
      value: threshold,
      maxValue
    })}`
  })

  return {
    displayThreshold,
    isNoValue,
    displayValue
  }
}

export const resolveBinaryProgressValue = (value = BINARY_VALUES.INCOMPLETE) => {
  const _value = isNumber(value) && !Number.isNaN(value) ? value : BINARY_VALUES.INCOMPLETE

  return _value >= BINARY_VALUES.COMPLETE ? BINARY_VALUES.COMPLETE : BINARY_VALUES.INCOMPLETE
}

export const getBinaryProgressLabelByValue = (value = 0) => {
  const resolvedValue = resolveBinaryProgressValue(value)
  const { label } = BINARY_OPTIONS.find(option => {
    return option.value === resolvedValue
  })

  return label
}

export const getProgressTableHeaderCellDisplayValue = ({
  granulationId = DEFAULT_THRESHOLD_PERIOD_GRANULATION,
  cellData = {}
} = {}) => {
  if (!granulationId || granulationId !== THRESHOLD_PERIOD_GRANULATION.WEEK.value) {
    return cellData[TARGET_PERIOD_ENTITY_KEYS.NAME] || ''
  }

  return createDatesRange(
    [
      cellData[TARGET_PERIOD_ENTITY_KEYS.START_DATE],
      cellData[TARGET_PERIOD_ENTITY_KEYS.END_DATE]
    ].map(d => dayjs(d).utc())
  )
}

const periodDurationCacheKeyResolver = period => {
  return `${period[TARGET_PERIOD_ENTITY_KEYS.START_DATE]}—${
    period[TARGET_PERIOD_ENTITY_KEYS.END_DATE]
  }`
}

export const getPeriodDuration = memoize((period = {}) => {
  if (
    isEmpty(period) ||
    !isObject(period) ||
    !has(period, TARGET_PERIOD_ENTITY_KEYS.START_DATE) ||
    !has(period, TARGET_PERIOD_ENTITY_KEYS.END_DATE)
  ) {
    return 0
  }

  const startDate = dayjs(period[TARGET_PERIOD_ENTITY_KEYS.START_DATE]).startOf(DATE_UNITS.DAY)
  const endDate = dayjs(period[TARGET_PERIOD_ENTITY_KEYS.END_DATE]).endOf(DATE_UNITS.DAY)

  return endDate.diff(startDate, DATE_UNITS.DAY)
}, periodDurationCacheKeyResolver)

export const useViewPointLastDate = ({
  periods = [],
  shouldStopWatcher = true,
  currentGranulationId
}) => {
  const initialViewPointLastDate = ref(null)

  const lastViewPoint = computed(() => {
    const resolvedPeriods = toValue(periods)

    if (isEmpty(resolvedPeriods)) return null

    return resolvedPeriods.at(-1)
  })

  const viewPointLastDate = computed(() => {
    if (!lastViewPoint.value) return undefined

    return (
      lastViewPoint.value[TARGET_PERIOD_ENTITY_KEYS.END_DATE] ||
      lastViewPoint.value.period[TARGET_PERIOD_ENTITY_KEYS.END_DATE]
    )
  })

  let stop, pause, resume
  const watchHandler = watch(
    lastViewPoint,
    newValue => {
      if (newValue && !initialViewPointLastDate.value) {
        initialViewPointLastDate.value = viewPointLastDate.value
      }

      if (initialViewPointLastDate.value) {
        if (shouldStopWatcher && stop) {
          stop()
        }

        if (!shouldStopWatcher && pause) {
          pause()
        }
      }
    },
    {
      immediate: true,
      deep: true
    }
  )
  stop = watchHandler.stop
  pause = watchHandler.pause
  resume = watchHandler.resume

  const resetInitialViewPoint = async () => {
    initialViewPointLastDate.value = null
    resume?.()
  }

  const isResetButtonDisabled = computed(() => {
    return (
      !initialViewPointLastDate.value || initialViewPointLastDate.value === viewPointLastDate.value
    )
  })

  const viewPointLastDatesForNavigation = computed(() => {
    const unitByGranulation = {
      [THRESHOLD_PERIOD_GRANULATION.WEEK.value]: DATE_UNITS.WEEK,
      [THRESHOLD_PERIOD_GRANULATION.MONTH.value]: DATE_UNITS.MONTH,
      [THRESHOLD_PERIOD_GRANULATION.QUARTER.value]: DATE_UNITS.QUARTER
    }

    const unitToCalculate = unitByGranulation[toValue(currentGranulationId)] || DATE_UNITS.MONTH

    return {
      forward: Number(
        dayjs(viewPointLastDate.value).add(1, unitToCalculate).subtract(1, DATE_UNITS.DAY)
      ),
      backward: Number(
        dayjs(viewPointLastDate.value).subtract(1, unitToCalculate).subtract(1, DATE_UNITS.DAY)
      )
    }
  })

  return {
    viewPointLastDate,
    initialViewPointLastDate,
    isResetButtonDisabled,
    resetInitialViewPoint,
    viewPointLastDatesForNavigation
  }
}

export const ELEMENT_PROGRESS_TABLE_DEFAULT_COLUMN_COUNT = 4

export const ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_OPTIONS = [3, 4, 5, 6, 7]
